import { Badge, Button } from "@/components/ui";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatDate } from "@/lib/utils/format";
import { getCalendarDaysUntilStatute } from "@/lib/utils/matters/statute-urgency-utils";
import { ChevronDown } from "lucide-react";
import { formatStatuteDaysRemainingLabel } from "../matter-overview-utils";

export function EarliestSolTopStat({
  statuteDate,
  statuteDaysRemaining,
  statuteConfirmed,
  statuteId,
  statutes,
  isExpanded,
  onExpandedChange,
  className,
}: {
  statuteDate?: string | Date;
  statuteDaysRemaining: number | null;
  statuteConfirmed?: boolean | null;
  statuteId?: string;
  statutes: Matter["statutesOfLimitations"];
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  className?: string;
}) {
  const daysRemainingLabel =
    formatStatuteDaysRemainingLabel(statuteDaysRemaining);
  const showConfirmationBadge = statuteDate != null;
  const additionalStatutes = statutes
    .filter((statute) => statute.id !== statuteId)
    .map((statute) => ({
      ...statute,
      daysRemaining: getCalendarDaysUntilStatute(statute.date),
    }))
    .sort((a, b) => {
      const aIsFuture = a.daysRemaining != null && a.daysRemaining >= 0;
      const bIsFuture = b.daysRemaining != null && b.daysRemaining >= 0;

      if (aIsFuture !== bIsFuture) {
        return aIsFuture ? -1 : 1;
      }

      if (a.daysRemaining == null || b.daysRemaining == null) {
        return 0;
      }

      return aIsFuture
        ? a.daysRemaining - b.daysRemaining
        : b.daysRemaining - a.daysRemaining;
    });
  const hasAdditionalStatutes = additionalStatutes.length > 0;

  return (
    <div
      className={cn("min-w-0 border-r px-3 py-3 last:border-r-0", className)}
    >
      <div className="flex items-center justify-between gap-1">
        <p className="text-muted-foreground text-[9px] font-semibold whitespace-nowrap">
          Earliest SOL
        </p>
        {hasAdditionalStatutes ? (
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground h-auto shrink-0 px-0 py-0 text-[10px] font-medium hover:bg-transparent"
            onClick={() => {
              onExpandedChange(!isExpanded);
            }}
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Show less" : "Show more"}
            <ChevronDown
              className={cn(
                "ml-0.5 h-3 w-3 transition-transform",
                isExpanded ? "rotate-180" : "",
              )}
            />
          </Button>
        ) : null}
      </div>
      <p className="text-foreground mt-1 text-[15px] leading-tight font-semibold">
        {formatDate(statuteDate) || "—"}
      </p>
      {daysRemainingLabel || showConfirmationBadge ? (
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
          {daysRemainingLabel ? <span>{daysRemainingLabel}</span> : null}
          {showConfirmationBadge ? (
            <Badge
              variant={statuteConfirmed ? "green" : "amber"}
              className="font-semibold"
            >
              {statuteConfirmed ? "Confirmed" : "Needs confirmation"}
            </Badge>
          ) : null}
        </div>
      ) : null}
      {isExpanded ? (
        <div className="mt-3 space-y-3 border-t pt-3">
          {additionalStatutes.map((statute) => {
            const isPast =
              statute.daysRemaining != null && statute.daysRemaining < 0;
            const additionalDaysLabel = formatStatuteDaysRemainingLabel(
              statute.daysRemaining,
            );

            return (
              <div
                key={statute.id}
                className={cn(
                  "space-y-0.5 text-xs",
                  isPast ? "text-muted-foreground" : "text-foreground",
                )}
              >
                <p className={cn("font-medium", isPast ? "opacity-70" : "")}>
                  {formatDate(statute.date) || "—"}
                </p>
                {additionalDaysLabel ? (
                  <p
                    className={cn(
                      isPast ? "opacity-70" : "text-muted-foreground",
                    )}
                  >
                    {additionalDaysLabel}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
